<template>
  <div class="rounded-lg border border-gray-200 bg-white p-4 shadow-xs" data-testid="qc-dropzone">
    <h3 class="mb-2.5 text-lg font-semibold text-gray-800">
      {{ $t('messages.evaluation.qualityCheck.dropzone.title') }}
    </h3>
    <p class="mb-2.5 text-sm text-gray-500">
      {{ $t('messages.evaluation.qualityCheck.dropzone.which') }}
    </p>

    <!-- The reason in plain Estonian, inside the card, the drop area still
         usable underneath: nothing is lost by a wrong file (design-spec §6). -->
    <div
      v-if="rejection"
      class="mb-3 rounded-md border border-gray-200 border-l-[3px] border-l-red-500 bg-white px-4 py-3 text-sm text-gray-700"
      role="alert"
      data-testid="qc-rejection"
    >
      {{ rejection }}
    </div>

    <!--
      The chooser deliberately carries NO `accept` filter. PrimeVue validated
      `accept` itself and printed its own ENGLISH message, which pre-empted the
      five Estonian rejection sentences (the RED-346 lesson). A bare input with
      no filter hands every file to the server, which decides the type and
      answers in Estonian. Do not "tidy this up".
    -->
    <div
      class="rounded-lg border-2 border-dashed px-5 py-8 text-center transition-colors"
      :class="dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'"
      data-testid="qc-drop-area"
      @dragenter.prevent="dragOver = true"
      @dragover.prevent="dragOver = true"
      @dragleave.prevent="dragOver = false"
      @drop.prevent="onDrop"
    >
      <p class="mb-1 text-base font-medium text-gray-800">
        {{ $t('messages.evaluation.qualityCheck.dropzone.drop') }}
      </p>
      <p class="mb-2 text-sm text-gray-500">
        {{ $t('messages.evaluation.qualityCheck.dropzone.or') }}
      </p>
      <Button
        :label="$t('messages.evaluation.qualityCheck.dropzone.choose')"
        icon="pi pi-upload"
        class="w-auto!"
        data-testid="qc-choose"
        @click="picker?.click()"
      />
      <input
        ref="picker"
        type="file"
        class="hidden"
        data-testid="qc-pick-input"
        @change="onPick"
      />
      <p class="mt-3 text-xs text-gray-400" data-testid="qc-rules">
        {{ $t('messages.evaluation.qualityCheck.dropzone.rules', { max: maxMegabytes }) }}
        {{ $t('messages.evaluation.qualityCheck.dropzone.retention', { days: retentionDays ?? '' }) }}
      </p>
    </div>

    <!--
      Way 2 — REdata composes the .docx from the evaluation itself — is NOT
      built. The evaluation export is driven from the evaluation page (ESG map
      captures + the form payload in useExportDetail.js), so this screen cannot
      start it, and wiring it would change the export every user runs. Shown
      disabled, in the prototype's place for it, so the appraiser knows it is
      coming. Its own ticket (RED-351 rework, 2026-09-24).
    -->
    <div class="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="qc-way2">
      <Button
        :label="$t('messages.evaluation.qualityCheck.dropzone.fromEvaluation')"
        icon="pi pi-file-word"
        severity="secondary"
        outlined
        size="small"
        disabled
        class="w-auto!"
        data-testid="qc-way2-button"
      />
      <span class="text-xs text-gray-400">
        {{ $t('messages.evaluation.qualityCheck.dropzone.fromEvaluationLater') }}
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, useTemplateRef } from 'vue'

/**
 * RED-351 rework — `Lae üles valmis hindamisaruanne` (prototype v3 drop zone):
 * drag-and-drop or the chooser, one file, handed to the page as-is.
 */
defineProps({
  rejection: { type: String, default: null },
  maxMegabytes: { type: Number, required: true },
  retentionDays: { type: Number, default: null },
})

const emit = defineEmits(['file'])

const picker = useTemplateRef('picker')
const dragOver = ref(false)

const onPick = event => {
  const file = event.target.files?.[0]
  // Clear the input so the SAME file can be chosen again after a rejection
  // (the RED-334 lesson: a chooser must never be left dead).
  event.target.value = ''
  if (file) {
    emit('file', file)
  }
}

const onDrop = event => {
  dragOver.value = false
  const file = event.dataTransfer?.files?.[0]
  if (file) {
    emit('file', file)
  }
}
</script>
